import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	MatStepper,
	MatStep,
	MatStepperIcon,
	MatStepLabel,
} from '@angular/material/stepper';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { AddDeviceDetailComponent } from './add-device-detail/add-device-detail.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { Device, SerialNumber } from 'src/app/models/data/device';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { MatPaginator } from '@angular/material/paginator';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Company } from 'src/app/models/data/company';
import {
	Observable,
	debounceTime,
	distinctUntilChanged,
	startWith,
	switchMap,
} from 'rxjs';
import {
	FormControl,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { TransferDeviceOwnershipErrors } from 'src/app/models/data/deviceTransfer';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AddDeviceConfirmationComponent } from './add-device-confirmation/add-device-confirmation.component';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

enum StepperPage {
	detail = 0,
	confirmation = 1,
}

@Component({
	selector: 'app-add-device-main-tab',
	templateUrl: './add-device-main-tab.component.html',
	styleUrls: [
		'./add-device-main-tab.component.scss',
		'../../../ui/common/main.scss',
		'../../../ui/common/stepper.scss',
	],
	imports: [
		NgClass,
		MatStepper,
		MatStep,
		MatStepperIcon,
		MatStepLabel,
		MatFormField,
		MatInput,
		MatAutocompleteTrigger,
		FormsModule,
		ReactiveFormsModule,
		MatAutocomplete,
		MatOption,
		MatError,
		AddDeviceDetailComponent,
		DataSetComponent_1,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		AddDeviceConfirmationComponent,
		AsyncPipe,
		DataSetLoadingRowComponent,
	],
})
export class AddDeviceMainTabComponent implements OnInit {
	@Output() addDeviceRequestStatusEvents =
		new EventEmitter<AddDeviceStatusEvent>();
	@Output() goBack = new EventEmitter<boolean>();
	@ViewChild(AddDeviceDetailComponent)
	addDeviceDetail?: AddDeviceDetailComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	// Is true if the user is "transferring devices" from one company to another
	// and is false if the user is "importing devices" into the system.
	@Input() isTransferringDevices = false;

	isOracle = false;
	serialNumberList: SerialNumber[] = [];
	dataSource = new MatTableDataSource<Device>();
	displayedColumns: string[] = [
		'serialNumber',
		'deviceType',
		'provisionMessage',
	];

	companyNameLabel = $localize`:@@addDeviceTab-companyNameLabel:Company Name`;
	importDevicesLabel = $localize`:@@addDeviceTab-importDevicesTitle:Import Devices`;
	transferDevicesLabel = $localize`:@@addDeviceTab-transferDevicesTitle:Transfer Devices`;
	addDeviceDescription = $localize`:@@addDeviceTab-importDevicesDescription:Please enter the serial numbers you wish to add, placing each number on its own line`;

	companyFormControl: FormControl;
	filteredCompanyOptions?: Observable<Company[]>;
	selectedCompany?: Company;
	errorsFromTransferredDevices?: TransferDeviceOwnershipErrors;
	userTypedInInput: boolean = false;

	getDeviceProvisionErrorLoadingKey = ApiLoadingKeys.getDeviceProvisionError;

	constructor(
		private readonly scService: SystemCapabilityService,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.isOracle = this.scService.isOracle;
		this.companyFormControl = new FormControl([null, [Validators.required]]);
	}

	ngOnInit() {
		this.clearAllInputs();
		this.filteredCompanyOptions = this.companyFormControl.valueChanges.pipe(
			startWith(''),
			debounceTime(300),
			distinctUntilChanged(),
			switchMap((keyword) => this.searchCompanies(keyword))
		);
	}

	movePrevious(stepper: MatStepper): void {
		this.goBack.emit(true);
	}

	moveToFreshStepOneScreen(stepper: MatStepper): void {
		this.clearAllInputs();
		stepper.reset(); // Resets the current step (resets step 2)
		stepper.previous(); // Goes back to step 1
	}

	clearAllInputs() {
		this.addDeviceDetail?.clearInput();
		this.companyFormControl.reset();
		this.companyFormControl.setValue('');
		this.selectedCompany = undefined;
	}

	moveNext(stepper: MatStepper): void {
		if (stepper.selectedIndex === StepperPage.detail) {
			if (this.isTransferringDevices) {
				// Transferring devices from SentriKey Access to another company
				this.transferDevices();
			} else {
				// Importing devices into SentriKey Access
				this.addDevice();
			}
			stepper.next();
		}
	}

	isValid(stepper: MatStepper): boolean {
		if (stepper.selectedIndex == StepperPage.detail) {
			return this.isTransferringDevices
				? (this.addDeviceDetail?.isValid ?? false) &&
						this.companyFormControl.valid &&
						this.isCompanyFormControlValid(this.companyFormControl)
				: (this.addDeviceDetail?.isValid ?? false);
		}
		return false;
	}

	async transferDevices(): Promise<void> {
		this.addDeviceRequestStatusEvents.emit({
			status: AddDeviceRequestStatus.loading,
			isTransferringDevices: true,
		});

		try {
			this.serialNumberList = this.addDeviceDetail?.serialNumberList ?? [];
			if (this.serialNumberList.length > 0) {
				const strSerialNumberList = this.serialNumberList.map(
					(serialNumObj) => serialNumObj.serialNumber
				);
				this.errorsFromTransferredDevices =
					await this.apiService.massTransferDeviceOwnership(
						strSerialNumberList,
						this.selectedCompany!.id
					);
				this.addDeviceRequestStatusEvents.emit({
					status: AddDeviceRequestStatus.success,
					isTransferringDevices: true,
					transferToCompanyName: this.selectedCompany?.name,
					data: {
						errorsFromTransferredDevices: this.errorsFromTransferredDevices,
					},
				});
			}
		} catch (err: any) {
			this.addDeviceRequestStatusEvents.emit({
				status: AddDeviceRequestStatus.failure,
			});
		}
	}

	async addDevice(): Promise<void> {
		this.addDeviceRequestStatusEvents.emit({
			status: AddDeviceRequestStatus.loading,
		});

		try {
			this.serialNumberList = this.addDeviceDetail?.serialNumberList ?? [];
			if (this.serialNumberList.length > 0) {
				const serialNumberErrorList: SerialNumber[] =
					await this.apiService.addDevice(this.serialNumberList);
				if (serialNumberErrorList.length > 0) {
					// show invalid serial nubmers to user
					this.addDeviceRequestStatusEvents.emit({
						status: AddDeviceRequestStatus.failure,
						data: { serialNumberErrorList: serialNumberErrorList },
					});
				} else {
					this.addDeviceRequestStatusEvents.emit({
						status: AddDeviceRequestStatus.success,
					});
				}
			}
		} catch (err: any) {
			this.addDeviceRequestStatusEvents.emit({
				status: AddDeviceRequestStatus.failure,
			});
		}
	}

	async checkError(): Promise<void> {
		// api call to get the devices with isProvisioned = 0
		await this.apiService.getDeviceProvisionError().then((res) => {
			this.dataSource.data = res;
		});
	}

	displayCompanyName = (): string => {
		return this.selectedCompany ? this.selectedCompany.name : '';
	};

	isCompanyFormControlValid(control: FormControl) {
		return typeof control.value === 'object' && !Array.isArray(control.value);
	}

	onCompanyNameTextChange(event: any, trigger: any) {
		this.userTypedInInput = true;
		if (event.target.value === this.selectedCompany?.name) {
			trigger.closePanel();
		} else {
			this.selectedCompany = undefined;
			trigger.openPanel();
		}
	}

	onCompanySelected(event: any) {
		this.selectedCompany = event.source.value;
		this.companyFormControl.setValue(
			this.selectedCompany && this.selectedCompany.name
				? this.selectedCompany.name
				: ''
		);
	}

	async searchCompanies(keyword: string) {
		const queryParameters: QueryParam[] = [
			{ param: 'companyName', value: keyword },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
			{ param: 'active', value: true },
		];
		return this.apiService.searchCompaniesPublicDirectory(queryParameters);
	}

	rowId(row: Device): string {
		return row?.id || 'unknown-id';
	}
}

export enum AddDeviceRequestStatus {
	loading,
	success,
	failure,
}

export interface AddDeviceStatusEvent {
	status: AddDeviceRequestStatus;
	isTransferringDevices?: boolean;
	transferToCompanyName?: string;
	data?: {
		serialNumberErrorList?: SerialNumber[];
		errorsFromTransferredDevices?: TransferDeviceOwnershipErrors;
	};
}
